import { createAction } from "@reduxjs/toolkit";
import {
  FETCH_ANSWER_FOR_SEEK_REQUEST,
  FETCH_SEARCH_ANSWER_FOR_SEEK_REQUEST,
  FETCH_ANSWER_FOR_SEEK_ASSET_REQUEST,
  FETCHING_ANSWER_FOR_SEEK_SUCCEEDED,
  FETCHING_ANSWER_FOR_SEEK_FAILED,
  FETCH_SEEK_SESSIONS_REQUEST,
  FETCHING_SEEK_SESSIONS_SUCCEEDED,
  FETCHING_SEEK_SESSIONS_FAILED,
  DELETE_SEEK_SESSION_REQUEST,
  DELETING_SEEK_SESSION_SUCCEEDED,
  DELETING_SEEK_SESSION_FAILED,
  DELETE_SEEK_SESSIONS_REQUEST,
  DELETING_SEEK_SESSIONS_SUCCEEDED,
  DELETING_SEEK_SESSIONS_FAILED,
  FETCH_SEEK_SESSION_REQUEST,
  FETCHING_SEEK_SESSION_SUCCEEDED,
  FETCHING_SEEK_SESSION_FAILED,
  FETCH_SEEK_EMAIL_RESPONSE,
  FETCHING_SEEK_EMAIL_RESPONSE_SUCCEEDED,
  FETCHING_SEEK_EMAIL_RESPONSE_FAILED,
  INVALIDATE_SEEK_EMAIL_RESPONSE,
  UPDATE_SEEK_FEEDBACK,
  UPDATING_SEEK_FEEDBACK_SUCCEEDED,
  UPDATING_SEEK_FEEDBACK_FAILED,
  FETCH_SEEK_LIMIT,
  FETCHING_SEEK_LIMIT_SUCCEEDED,
  FETCHING_SEEK_LIMIT_FAILED,
  FETCH_SEEK_PROMPTS,
  FETCHING_SEEK_PROMPTS_SUCCEEDED,
  FETCHING_SEEK_PROMPTS_FAILED,
  INVALIDATE_SEEK_SESSION,
  TOGGLE_SEEK,
  FETCH_ASSET_SUMMARY,
  FETCHING_ASSET_SUMMARY_SUCCEEDED,
  FETCHING_ASSET_SUMMARY_FAILED,
} from "./actionTypes";
import {
  SeekAnswerRequest,
  SeekAnswerAssetRequest,
  SeekError,
  SeekAnswerResponse,
  SeekSessionsRequest,
  SeekSessionRequest,
  SeekEmailRequest,
  SeekEmailResponse,
  SeekFeedbackRequest,
  SeekAssetSummaryRequest,
  SeekAssetSummaryResponse,
  SeekAssetSummaryErrorResponse,
  SeekAnswerErrorResponse,
} from "src/types/seek";
import axios from "axios";
import { call, put, takeEvery } from "redux-saga/effects";

/**
 * With axios `responseType: "stream"` + fetch adapter, failed responses often expose
 * `error.response.data` as a ReadableStream. Drain it and parse JSON (or null if not JSON).
 */
function parseJsonFromReadableStream(
  stream: ReadableStream<Uint8Array>,
): Promise<unknown> {
  return new Response(stream).text().then((text) => {
    try {
      return JSON.parse(text);
    } catch {
      return null;
    }
  });
}

function isReadableStreamBody(value: unknown): value is ReadableStream<Uint8Array> {
  return (
    value != null &&
    typeof (value as ReadableStream<Uint8Array>).getReader === "function"
  );
}

const SEEK_LEGACY_URL = `${process.env.REACT_APP_API_URL}/api/2.0/seek`;
const SEEK_URL = `${process.env.REACT_APP_SEEK_API_URL}/seek/3.0`;
const SEEK_ANSWER_ASSET_URL = `${process.env.REACT_APP_SEEK_API_URL}/seek/3.0/answer/asset`;


export const fetchAnswerForSeek = createAction<SeekAnswerRequest>(
  FETCH_ANSWER_FOR_SEEK_REQUEST,
);

export const fetchSearchAnswerForSeek = createAction<SeekAnswerRequest>(
  FETCH_SEARCH_ANSWER_FOR_SEEK_REQUEST,
);

const fetchingAnswerForSeekSucceeded = createAction<{
  uuid: string;
  response: SeekAnswerResponse;
}>(FETCHING_ANSWER_FOR_SEEK_SUCCEEDED);
const fetchingAnswerForSeekFailed = createAction<SeekAnswerErrorResponse>(
  FETCHING_ANSWER_FOR_SEEK_FAILED,
);

function* runSeekAnswerStream(
  url: string,
  chatUuid: string | null | undefined,
  data: Record<string, unknown>,
): any {
  const fetchAnswerApi = () => {
    return axios({
      method: "POST",
      url,
      data,
      responseType: "stream",
      headers: {
        "Content-Type": "application/json",
        Accept: "text/event-stream",
      },
      adapter: "fetch",
    }).then((response) => response.data);
  };

  let reader: ReadableStreamDefaultReader<Uint8Array> | null = null;
  try {
    const response = yield call(fetchAnswerApi);
    reader = response.pipeThrough(new TextDecoderStream()).getReader();
    let buffer = "";
    while (true) {
      const { done, value } = yield reader!.read();
      if (done) break;

      buffer += value;
      const lines = buffer.split("\n");
      buffer = lines.pop() || "";

      for (let i = 0; i < lines.length; i++) {
        const line = lines[i];

        if (line.startsWith("data:")) {
          const dataContent = line.substring(5).trim();

          if (dataContent) {
            const parsed = JSON.parse(dataContent);
            yield put(
              fetchingAnswerForSeekSucceeded({
                uuid: String(chatUuid),
                response: parsed,
              }),
            );
          }
        }
      }
    }
  } catch (error: any) {
    const status = error?.response?.status ?? 0;
    let message: string = error?.message ?? String(error);
    let code: string | number = status;

    const responseData = error;

    if (isReadableStreamBody(responseData)) {
      try {
        const parsed: any = yield call(parseJsonFromReadableStream, responseData);
        if (parsed?.errors?.[0]) {
          message = parsed.errors[0].message ?? message;
          const c = parsed.errors[0].code;
          code = c != null ? c : code;
        } else if (parsed && typeof parsed === "object") {
          if ("message" in parsed) {
            message = String((parsed as { message: unknown }).message);
          }
          if ("code" in parsed) {
            const c = (parsed as { code: unknown }).code;
            code = c != null && (typeof c === "number" || typeof c === "string") ? c : code;
          }
        }
      } catch {
        /* keep axios message / status */
      }
    } else if (error && Array.isArray(error.errors) && error.errors[0]) {
      message = error.errors[0].message ?? message;
      const c = error.errors[0].code;
      code = c != null ? c : code;
    } else if (responseData && typeof responseData === "object" && responseData.errors?.[0]) {
      message = responseData.errors[0].message ?? message;
      const c = responseData.errors[0].code;
      code = c != null ? c : code;
    } else if (typeof responseData === "string") {
      try {
        const parsed = JSON.parse(responseData);
        if (parsed?.errors?.[0]) {
          message = parsed.errors[0].message;
          const c = parsed.errors[0].code;
          code = c != null ? c : code;
        }
      } catch {
        /* ignore */
      }
    }

    const payload: SeekAnswerErrorResponse = {
      uuid: String(chatUuid),
      errors: [{ message, code }],
    };
    yield put(fetchingAnswerForSeekFailed(payload));
  }
}

const fetchAnswerForSeekSaga = function* (
  action: ReturnType<typeof fetchAnswerForSeek>,
): any {
  const { uuid, session, question, request } = action.payload;

  let data: Record<string, unknown> = {
    question,
  };

  if (session) {
    data.session = session;
  }

  if (request) {
    data = {
      ...data,
      ...request,
    };
  }

  yield* runSeekAnswerStream(`${SEEK_URL}/answer`, uuid, data);
};

const fetchSearchAnswerForSeekSaga = function* (
  action: ReturnType<typeof fetchSearchAnswerForSeek>,
): any {
  const { uuid, session, question, request } = action.payload;

  let data: Record<string, unknown> = {
    question,
  };

  if (session) {
    data.session = session;
  }

  if (request) {
    data = {
      ...data,
      ...request,
    };
  }

  yield* runSeekAnswerStream(`${SEEK_URL}/answer/search`, uuid, data);
};

export const fetchAnswerForSeekAsset = createAction<SeekAnswerAssetRequest>(
  FETCH_ANSWER_FOR_SEEK_ASSET_REQUEST,
);

const fetchAnswerForSeekAssetSaga = function* (
  action: ReturnType<typeof fetchAnswerForSeekAsset>,
): any {
  const { uuid, session, question, content } = action.payload;

  const data: {
    question: string;
    content: { type: string; id: string };
    session?: string;
  } = {
    question,
    content,
  };

  if (session) {
    data.session = session;
  }

  yield* runSeekAnswerStream(SEEK_ANSWER_ASSET_URL, uuid, data);
};

export const fetchSeekSessions = createAction<SeekSessionsRequest>(
  FETCH_SEEK_SESSIONS_REQUEST,
);

const fetchingSeekSessionsSucceeded = createAction(
  FETCHING_SEEK_SESSIONS_SUCCEEDED,
);

const fetchingSeekSessionsFailed = createAction<SeekError>(
  FETCHING_SEEK_SESSIONS_FAILED,
);

const fetchSeekSessionsSaga = function* (
  action: ReturnType<typeof fetchSeekSessions>,
): any {
  const { page = 0, limit = 10 } = action.payload || {};

  const fetchSeekSessionsApi = () => {
    return axios({
      method: "GET",
      url: `${SEEK_LEGACY_URL}/sessions`,
      params: {
        page: page,
        limit: limit,
      },
    }).then((response) => response.data);
  };

  try {
    const response = yield call(fetchSeekSessionsApi);
    yield put(fetchingSeekSessionsSucceeded(response));
  } catch (error) {
    yield put(fetchingSeekSessionsFailed(error as SeekError));
  }
};

export const deleteSeekSession = createAction<SeekSessionRequest>(
  DELETE_SEEK_SESSION_REQUEST,
);

const deletingSeekSessionSucceeded = createAction<SeekSessionRequest>(
  DELETING_SEEK_SESSION_SUCCEEDED,
);

const deletingSeekSessionFailed = createAction<SeekError>(
  DELETING_SEEK_SESSION_FAILED,
);

const deleteSeekSessionSaga = function* (
  action: ReturnType<typeof deleteSeekSession>,
): any {
  const { id } = action.payload;

  const deleteSeekSessionApi = () => {
    return axios({
      method: "DELETE",
      url: `${SEEK_LEGACY_URL}/sessions/${id}`,
    }).then((response) => response.data);
  };

  try {
    yield call(deleteSeekSessionApi);
    yield put(deletingSeekSessionSucceeded({ id }));
  } catch (error) {
    yield put(deletingSeekSessionFailed(error as SeekError));
  }
};

export const deleteSeekSessions = createAction<void>(
  DELETE_SEEK_SESSIONS_REQUEST,
);

const deletingSeekSessionsSucceeded = createAction<void>(
  DELETING_SEEK_SESSIONS_SUCCEEDED,
);

const deletingSeekSessionsFailed = createAction<SeekError>(
  DELETING_SEEK_SESSIONS_FAILED,
);

const deleteSeekSessionsSaga = function* (): any {

  const deleteSeekSessionsApi = () => {
    return axios({
      method: "DELETE",
      url: `${SEEK_LEGACY_URL}/sessions`,
    }).then((response) => response.data);
  };

  try {
    yield call(deleteSeekSessionsApi);
    yield put(deletingSeekSessionsSucceeded());
  } catch (error) {
    yield put(deletingSeekSessionsFailed(error as SeekError));
  }
};

export const fetchSeekSession = createAction<SeekSessionRequest>(
  FETCH_SEEK_SESSION_REQUEST,
);

const fetchingSeekSessionSucceeded = createAction<SeekAnswerResponse>(
  FETCHING_SEEK_SESSION_SUCCEEDED,
);

const fetchingSeekSessionFailed = createAction<SeekError>(
  FETCHING_SEEK_SESSION_FAILED,
);

export const invalidateSeekSession = createAction(INVALIDATE_SEEK_SESSION);

const fetchSeekSessionSaga = function* (
  action: ReturnType<typeof fetchSeekSession>,
): any {
  const { id } = action.payload;

  const fetchSeekSessionApi = () => {
    return axios({
      method: "GET",
      url: `${SEEK_URL}/sessions/${id}/chats`,
    }).then((response) => response.data);
  };

  try {
    const response = yield call(fetchSeekSessionApi);
    yield put(fetchingSeekSessionSucceeded(response));
  } catch (error) {
    yield put(fetchingSeekSessionFailed(error as SeekError));
  }
};

export const fetchSeekEmailResponse = createAction<SeekEmailRequest>(
  FETCH_SEEK_EMAIL_RESPONSE,
);

const fetchingSeekEmailResponseSucceeded = createAction<SeekEmailResponse>(
  FETCHING_SEEK_EMAIL_RESPONSE_SUCCEEDED,
);

const fetchingSeekEmailResponseFailed = createAction<SeekError>(
  FETCHING_SEEK_EMAIL_RESPONSE_FAILED,
);

export const invalidateSeekEmailResponse = createAction(
  INVALIDATE_SEEK_EMAIL_RESPONSE,
);

const fetchSeekEmailResponseSaga = function* (
  action: ReturnType<typeof fetchSeekEmailResponse>,
): any {
  const { id, chatId } = action.payload;

  const fetchSeekEmailResponseApi = () => {
    return axios({
      method: "GET",
      url: `${SEEK_URL}/sessions/${id}/chats/${chatId}/email`,
    }).then((response) => response.data);
  };

  try {
    const response = yield call(fetchSeekEmailResponseApi);
    yield put(fetchingSeekEmailResponseSucceeded(response));
  } catch (error) {
    yield put(fetchingSeekEmailResponseFailed(error as SeekError));
  }
};

export const updateSeekFeedback =
  createAction<SeekFeedbackRequest>(UPDATE_SEEK_FEEDBACK);

const updatingSeekHelpfulSucceeded = createAction<any>(
  UPDATING_SEEK_FEEDBACK_SUCCEEDED,
);

const updatingSeekHelpfulFailed = createAction<SeekError>(
  UPDATING_SEEK_FEEDBACK_FAILED,
);

const updateSeekFeedbackSaga = function* (
  action: ReturnType<typeof updateSeekFeedback>,
): any {
  const { isHelpful, chatId } = action.payload;

  const updateSeekFeedbackApi = () => {
    return axios({
      method: "PUT",
      url: `${SEEK_URL}/chats/${chatId}/feedback`,
      params: {
        isHelpful: isHelpful,
      },
    }).then((response) => response.data);
  };

  try {
    const response = yield call(updateSeekFeedbackApi);
    yield put(updatingSeekHelpfulSucceeded(response));
  } catch (error) {
    yield put(updatingSeekHelpfulFailed(error as SeekError));
  }
};

export const fetchSeekLimit = createAction(FETCH_SEEK_LIMIT);

const fetchingSeekLimitSucceeded = createAction<any>(
  FETCHING_SEEK_LIMIT_SUCCEEDED,
);

const fetchingSeekLimitFailed = createAction<SeekError>(
  FETCHING_SEEK_LIMIT_FAILED,
);

const fetchSeekLimitSaga = function* (): any {
  const fetchSeekLimitApi = () => {
    return axios({
      method: "GET",
      url: `${SEEK_URL}/limit`,
    }).then((response) => response.data);
  };

  try {
    const response = yield call(fetchSeekLimitApi);
    yield put(fetchingSeekLimitSucceeded(response));
  } catch (error) {
    yield put(fetchingSeekLimitFailed(error as SeekError));
  }
};

export const fetchSeekPrompts = createAction(FETCH_SEEK_PROMPTS);

const fetchingSeekPromptsSucceeded = createAction<any>(
  FETCHING_SEEK_PROMPTS_SUCCEEDED,
);

const fetchingSeekPromptsFailed = createAction<SeekError>(
  FETCHING_SEEK_PROMPTS_FAILED,
);

const fetchSeekPromptsSaga = function* (): any {
  const fetchSeekPromptsApi = () => {
    return axios({
      method: "GET",
      url: `${SEEK_URL}/suggestions`,
    }).then((response) => response.data);
  };

  try {
    const response = yield call(fetchSeekPromptsApi);
    yield put(fetchingSeekPromptsSucceeded(response));
  } catch (error) {
    yield put(fetchingSeekPromptsFailed(error as SeekError));
  }
};

export const fetchAssetSummary = createAction<SeekAssetSummaryRequest>(FETCH_ASSET_SUMMARY);

const fetchingAssetSummarySucceeded = createAction<{
  uuid: string | null;
  response: SeekAssetSummaryResponse;
}>(
  FETCHING_ASSET_SUMMARY_SUCCEEDED
);

const fetchingAssetSummaryFailed = createAction<SeekAssetSummaryErrorResponse>(
  FETCHING_ASSET_SUMMARY_FAILED,
);

const fetchAssetSummarySaga = function* (action: ReturnType<typeof fetchAssetSummary>): any {

  const fetchAssetSummaryApi = () => {
    return axios({
      method: "POST",
      url: `${SEEK_LEGACY_URL}/asset_summary`,
      data: action.payload
    }).then((response) => response.data);
  };

  try {
    const response = yield call(fetchAssetSummaryApi);
    yield put(fetchingAssetSummarySucceeded({
      uuid: null,
      response
    }));
  } catch (error) {
    yield put(fetchingAssetSummaryFailed(error as SeekError));
  }
};

export const toggleSeek = createAction(TOGGLE_SEEK);

export default function* seekSaga() {
  yield takeEvery(FETCH_ANSWER_FOR_SEEK_REQUEST, fetchAnswerForSeekSaga);
  yield takeEvery(FETCH_SEARCH_ANSWER_FOR_SEEK_REQUEST, fetchSearchAnswerForSeekSaga);
  yield takeEvery(FETCH_ANSWER_FOR_SEEK_ASSET_REQUEST, fetchAnswerForSeekAssetSaga);
  yield takeEvery(FETCH_SEEK_SESSIONS_REQUEST, fetchSeekSessionsSaga);
  yield takeEvery(DELETE_SEEK_SESSION_REQUEST, deleteSeekSessionSaga);
  yield takeEvery(FETCH_SEEK_SESSION_REQUEST, fetchSeekSessionSaga);
  yield takeEvery(FETCH_SEEK_EMAIL_RESPONSE, fetchSeekEmailResponseSaga);
  yield takeEvery(UPDATE_SEEK_FEEDBACK, updateSeekFeedbackSaga);
  yield takeEvery(FETCH_SEEK_LIMIT, fetchSeekLimitSaga);
  yield takeEvery(FETCH_SEEK_PROMPTS, fetchSeekPromptsSaga);
  yield takeEvery(DELETE_SEEK_SESSIONS_REQUEST, deleteSeekSessionsSaga);
  yield takeEvery(FETCH_ASSET_SUMMARY, fetchAssetSummarySaga);
}
